import { Component, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Refresh as RefreshIcon, Update as UpdateIcon } from '@mui/icons-material'
import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { hideAllDrawers } from '@/app/slices/appSlice'
import { store } from '@/app/store'
import { getDeploymentInfo, handleDeploymentMismatch } from '@/common/utils/deploymentManager'
import { isDeploymentError } from '@/common/utils/dynamicImportWithRetry'

// Error boundary wrapper class (minimal class component for error catching)
class ChunkLoadErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
        this.drawerHideDispatched = false
    }

    static getDerivedStateFromError(error) {
        // Check if this is a chunk loading error (including our enhanced retry errors)
        if (
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes('Loading CSS chunk') ||
            error?.name === 'ChunkLoadError' ||
            error?.name === 'DynamicImportError' ||
            error?.message?.includes('Dynamic import failed after') ||
            error?.message?.includes('Failed to fetch')
        ) {
            return { hasError: true, error }
        }
        return null
    }

    componentDidCatch(error, errorInfo) {
        // Log chunk loading errors for monitoring
        console.error('Chunk loading error:', error, errorInfo)

        // Hide all drawers when chunk loading error occurs (only dispatch once)
        const { hasError } = this.state
        if (!this.drawerHideDispatched && hasError) {
            store.dispatch(hideAllDrawers())
            this.drawerHideDispatched = true
        }

        // Enhanced logging for retry-aware errors
        if (error?.name === 'DynamicImportError') {
            console.error('Retry information:', {
                componentName: error.componentName,
                attemptCount: error.attemptCount,
                originalError: error.originalError,
            })
        }

        // Send to Application Insights with enhanced retry information
        if (window.appInsights) {
            window.appInsights.trackException({
                exception: error,
                properties: {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: 'ChunkLoadError',
                    chunkError: true,
                    // Enhanced retry context
                    componentName: error?.componentName || 'Unknown',
                    retryAttempts: error?.attemptCount || 1,
                    wasRetried: error?.name === 'DynamicImportError',
                    originalErrorMessage: error?.originalError?.message || error?.message,
                },
            })
        }
    }

    render() {
        const { hasError } = this.state
        const { children } = this.props

        if (hasError) {
            const { error } = this.state
            return <ChunkLoadErrorUI error={error} />
        }

        return children
    }
}

// Functional component for the UI
const ChunkLoadErrorUI = ({ error }) => {
    const { t } = useTranslation()
    const [reloadFailed, setReloadFailed] = useState(false)

    // Extract retry information if available
    const isRetryError = error?.name === 'DynamicImportError'
    const isDeploymentMismatch = error?.name === 'DeploymentMismatchError' || isDeploymentError(error)
    const requiresAutomaticReload = error?.requiresAutomaticReload === true
    const componentName = error?.componentName || 'component'
    const attemptCount = error?.attemptCount || 1
    const wasRetried = isRetryError && attemptCount > 1

    // Get deployment information for better error context
    const deploymentInfo = getDeploymentInfo()

    // Deployment errors get an automatic reload attempt first; anything else shows the button at once.
    const needsAutomaticReload = (isDeploymentMismatch && !deploymentInfo.isConsistent) || requiresAutomaticReload
    const automaticReloadState = !needsAutomaticReload ? 'not-needed' : reloadFailed ? 'failed' : 'attempting'
    const showReloadButton = !needsAutomaticReload || reloadFailed

    // Handle automatic reload attempts for deployment errors
    useEffect(() => {
        if (!needsAutomaticReload) {
            return undefined
        }

        // Wait a short time to allow the error to be displayed, then attempt automatic reload
        const autoReloadTimeout = setTimeout(() => {
            try {
                handleDeploymentMismatch('Automatic recovery from chunk load error', {
                    originalError: error?.message || '',
                    errorType: error?.name || '',
                    componentName,
                    attemptCount,
                    isAutomatic: true,
                })
            } catch (reloadError) {
                // If automatic reload fails, show the button
                console.warn('Automatic reload failed:', reloadError)
                setReloadFailed(true)
            }
        }, 1500) // 1.5 second delay to show error message first

        // Set a timeout for automatic reload attempt failure
        const failureTimeout = setTimeout(() => {
            setReloadFailed(true)
        }, 8000) // 8 seconds total for automatic reload attempt

        return () => {
            clearTimeout(autoReloadTimeout)
            clearTimeout(failureTimeout)
        }
    }, [needsAutomaticReload, error, componentName, attemptCount])

    // Determine error type and messaging
    const getErrorInfo = () => {
        // Prioritize retry errors over deployment detection for DynamicImportError
        if (wasRetried) {
            return {
                type: 'network',
                title: 'Connection Issue',
                message: `Failed to load ${componentName} after ${attemptCount} attempts. This appears to be a network connectivity issue.`,
                icon: <RefreshIcon />,
                severity: 'warning',
                actionText: 'Try Again',
                helpText: 'If this problem persists, please check your internet connection or try again later.',
            }
        }

        if (isDeploymentMismatch || !deploymentInfo.isConsistent) {
            return {
                type: 'deployment',
                title: 'Application Updated',
                message: `The application has been updated while you were using it. Please refresh to get the latest version.`,
                icon: <UpdateIcon />,
                severity: 'info',
                actionText: 'Update Now',
                helpText: 'This will reload the page with the latest application files.',
            }
        }

        return {
            type: 'general',
            title: 'Loading Error',
            message: 'There was a problem loading this page. This usually happens when the app has been updated.',
            icon: <RefreshIcon />,
            severity: 'warning',
            actionText: 'Reload Page',
            helpText: 'If this problem persists, please clear your browser cache or contact support.',
        }
    }

    const errorInfo = getErrorInfo()

    const handleReload = () => {
        if (errorInfo.type === 'deployment') {
            // Use deployment manager for proper recovery
            handleDeploymentMismatch('User triggered recovery from chunk load error', {
                originalError: error?.message || '',
                errorType: error?.name || '',
                componentName,
                attemptCount,
            })
        } else {
            // Standard cache clearing and reload
            if ('caches' in window) {
                try {
                    caches
                        .keys()
                        .then((names) => {
                            names.forEach((name) => {
                                if (name.includes('vite') || name.includes('workbox')) {
                                    caches.delete(name)
                                }
                            })
                        })
                        .catch(() => {
                            // Ignore cache clearing errors
                        })
                } catch {
                    // Ignore cache clearing errors
                }
            }

            // Reload the page to fetch fresh chunks
            window.location.reload()
        }
    }

    return (
        <Container
            maxWidth="sm"
            sx={{ mt: 4, textAlign: 'center' }}
        >
            <Alert
                icon={false}
                severity={t(errorInfo.severity)}
                sx={{ mb: 3 }}
            >
                <Typography
                    component="div"
                    variant="h6"
                    gutterBottom
                >
                    {t(errorInfo.title)}
                </Typography>
                <Typography variant="body2">{t(errorInfo.message)}</Typography>
            </Alert>

            {/* Show automatic reload progress for deployment errors */}
            {automaticReloadState === 'attempting' ? (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <CircularProgress
                            size={20}
                            sx={{ mr: 1 }}
                        />
                        <Typography
                            color="text.secondary"
                            variant="body2"
                        >
                            {t('Attempting automatic reload...')}
                        </Typography>
                    </Box>
                    <Typography
                        color="text.secondary"
                        variant="caption"
                    >
                        {t("If this doesn't work, a reload button will appear shortly.")}
                    </Typography>
                </Box>
            ) : null}

            {/* Show reload button only after automatic reload fails or for non-deployment errors */}
            {showReloadButton ? (
                <Button
                    size="large"
                    startIcon={errorInfo.icon}
                    variant="contained"
                    onClick={handleReload}
                >
                    {t(errorInfo.actionText)}
                </Button>
            ) : null}

            {showReloadButton ? (
                <Typography
                    color="text.secondary"
                    sx={{ mt: 2 }}
                    variant="body2"
                >
                    {t(errorInfo.helpText)}
                </Typography>
            ) : null}

            {/* Show debug info in development */}
            {process.env.NODE_ENV === 'development' ? (
                <Typography
                    color="text.secondary"
                    sx={{ mt: 3, fontSize: '0.75rem' }}
                    variant="body2"
                >
                    Debug: {errorInfo.type} error | Version: {deploymentInfo.currentVersion} |{' '}
                    {deploymentInfo.isConsistent ? 'Consistent' : 'Inconsistent'} | Reload State: {automaticReloadState}{' '}
                    | Show Button: {showReloadButton.toString()}
                </Typography>
            ) : null}
        </Container>
    )
}

// Main export uses the boundary but maintains the same API
const ChunkLoadError = ({ children }) => {
    return <ChunkLoadErrorBoundary>{children}</ChunkLoadErrorBoundary>
}

ChunkLoadError.propTypes = {
    children: PropTypes.node.isRequired,
}

ChunkLoadErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
}

ChunkLoadErrorUI.propTypes = {
    error: PropTypes.object,
}

export default ChunkLoadError
